/**
 * Widget Account Selection (Story 32.6)
 *
 * Pure selection algebra for the per-widget account override picker — the
 * editor-side companion of the server's `integrationIds` filter
 * (apps/server/src/services/widget-populate.ts, Story 31-30). No React, no
 * fetch: every rule that decides WHAT the picker writes lives here so it can
 * be verified in isolation (build-to-sell: the selection contract is testable
 * without mounting the editor).
 *
 * ── Value-space contract (Story 31-30 / Trap #1) ───────────────────────────
 * `config.integrationId` (singular) holds a PLATFORM SLUG ("google-analytics-4").
 * `config.integrationIds` (plural) holds `integrations` ROW UUIDs — the ids on
 * `useClientIntegrations` rows. This module derives PLATFORMS from the widget
 * config, but only ever emits ROW UUIDs for writing.
 *
 * ── Server semantics this module must respect (31-30 review hand-off) ──────
 * - key absent  → all-attachments aggregate (default; future attachments
 *   auto-include) — so an all-on selection DELETES the key (never pins it).
 * - `[]`        → server-normalized to "ALL accounts" — NEVER written here.
 * - one flat list serves EVERY platform the widget consults; a consulted
 *   platform with zero ids in the list is silently dropped on cross-platform
 *   paths — so every involved platform must contribute ≥1 id.
 * - schema cap: `z.array(z.string().min(1)).max(20)` — a >20-id list would
 *   fail report-save validation, so it is never written.
 */

import { METRIC_PLATFORM_MAP } from "./templateUtils";

// ============================================================================
// Types
// ============================================================================

/** The slice of a `useClientIntegrations` row this module consumes. */
export interface AttachmentRow {
  /** `integrations` table ROW UUID — the value space `integrationIds` stores. */
  id: string;
  /** Platform slug (the API's `type` field), e.g. "google-analytics-4". */
  type: string;
  accountName: string | null;
  syncStatus: string | null;
}

/** Minimal widget shape needed to derive its involved platforms. */
export interface WidgetLike {
  type: string;
  config: Record<string, unknown>;
}

/** Non-paused attachments of one involved platform, in API row order. */
export interface PlatformAttachmentGroup {
  platform: string;
  attachments: AttachmentRow[];
}

/** Outcome of a selection write decision (AC #5). */
export type SelectionPatchResult =
  /** Every attachment selected → DELETE `integrationIds` (default-all). */
  | { kind: "delete" }
  /** Proper subset → write these row UUIDs (never empty, ≤ 20). */
  | { kind: "write"; integrationIds: string[] }
  /** Union would exceed the 20-id schema cap → do not write (AC #5e). */
  | { kind: "too-many" };

// ============================================================================
// Constants
// ============================================================================

/**
 * Prefix of the cross-tool `resolve:<platform>` sentinels
 * (widgetPresets.ts — CROSS_TOOL_RESOLVE_SENTINEL_*). The server's
 * synthetic-aggregate branch ignores `integrationIds` entirely for these
 * widgets (31-30 AC #12c), so they are excluded from involvement.
 */
const CROSS_TOOL_RESOLVE_PREFIX = "resolve:";

/**
 * The social platforms a top-posts widget with `platform: "all"` consults.
 * GROUND TRUTH: mirrors the server's fixed list in widget-populate.ts
 * (`getWidgetPlatform` + the top-posts "all" aggregation branch) — NOT "every
 * connected platform of the client". Keeping parity with the server candidate
 * set is what makes the indicator truthful.
 */
export const TOP_POSTS_SOCIAL_PLATFORMS: readonly string[] = [
  "facebook-pages",
  "instagram-business",
  "linkedin-pages",
];

/** Schema cap on `integrationIds` (packages/db report-layout-types.ts). */
export const INTEGRATION_IDS_MAX = 20;

// ============================================================================
// Involved-platform derivation (AC #6a)
// ============================================================================

/** True when the id is a `resolve:<platform>` cross-tool sentinel. */
export function isCrossToolResolveSentinel(integrationId: unknown): boolean {
  return (
    typeof integrationId === "string" &&
    integrationId.startsWith(CROSS_TOOL_RESOLVE_PREFIX)
  );
}

/** Valid, non-sentinel platform slug or null. */
function asPlatform(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;
  if (isCrossToolResolveSentinel(value)) return null;
  return value;
}

/**
 * The set of platforms a widget's data fetch consults, in stable order.
 * Mirrors the server's resolution (widget-populate.ts `getWidgetPlatform` /
 * `resolveMetricPlatforms`):
 *
 * - top-posts: `config.platform` — a specific social platform, or `"all"` →
 *   the fixed social trio intersected with the client's connected platforms.
 * - multi-metric (line-chart / data-table): union over the widget's metrics of
 *   sidecar (`config.metricIntegrations[key]`) → `METRIC_PLATFORM_MAP[key]` →
 *   current `config.integrationId` (the DataSourceSection `platformFor` chain).
 *   data-table additionally consults `config.columns` (known metrics only).
 * - everything else (single-metric): `config.integrationId`.
 *
 * `resolve:<platform>` sentinels are excluded everywhere — a widget whose data
 * source is a sentinel derives to [] (no indicator, 31-30 AC #12c).
 */
export function deriveInvolvedPlatforms(
  widget: WidgetLike,
  connectedPlatforms: readonly string[],
): string[] {
  const config = widget.config ?? {};

  if (widget.type === "top-posts") {
    const configPlatform = config.platform;
    if (
      typeof configPlatform === "string" &&
      configPlatform !== "all" &&
      TOP_POSTS_SOCIAL_PLATFORMS.includes(configPlatform)
    ) {
      return [configPlatform];
    }
    // "all", absent, or ANY unrecognized value: the server's
    // `getWidgetPlatform` resolves non-social/invalid values to undefined,
    // which routes to the all-connected-social aggregation branch — mirror
    // it (review R3-1).
    const connected = new Set(connectedPlatforms);
    return TOP_POSTS_SOCIAL_PLATFORMS.filter((p) => connected.has(p));
  }

  const integrationId = asPlatform(config.integrationId);
  // A sentinel data source opts the whole widget out (server ignores the
  // filter for synthetic aggregates) — even if it also carries metrics.
  if (isCrossToolResolveSentinel(config.integrationId)) return [];

  const isMultiMetric =
    widget.type === "line-chart" || widget.type === "data-table";
  if (!isMultiMetric) {
    return integrationId ? [integrationId] : [];
  }

  const sidecar = (config.metricIntegrations ?? {}) as Record<string, unknown>;
  const platformFor = (key: string): string | null =>
    asPlatform(sidecar[key]) ??
    asPlatform(METRIC_PLATFORM_MAP[key]) ??
    integrationId;

  const involved = new Set<string>();
  const raw = config.metrics;
  if (Array.isArray(raw)) {
    for (const m of raw) {
      if (typeof m === "string" && m) {
        const p = platformFor(m);
        if (p) involved.add(p);
      } else if (m && typeof m === "object") {
        const o = m as { key?: unknown; integrationId?: unknown };
        if (typeof o.key === "string" && o.key) {
          const p = asPlatform(o.integrationId) ?? platformFor(o.key);
          if (p) involved.add(p);
        }
      }
    }
  }
  // data-table renders from `config.columns` (string[]); a template-seeded
  // table may carry columns without a `metrics` array.
  if (widget.type === "data-table" && Array.isArray(config.columns)) {
    for (const col of config.columns) {
      if (typeof col === "string" && METRIC_PLATFORM_MAP[col]) {
        const p = platformFor(col);
        if (p) involved.add(p);
      }
    }
  }
  if (involved.size === 0 && integrationId) involved.add(integrationId);
  return [...involved];
}

// ============================================================================
// Attachment grouping / counting (AC #1, #2)
// ============================================================================

/** True when the attachment participates in server aggregation (31-30 AC #5
 *  excludes only `paused` from the candidate set — auth_failed /
 *  resource_unavailable rows are still attempted). */
function isCandidate(row: AttachmentRow): boolean {
  return row.syncStatus !== "paused";
}

/**
 * Group the client's NON-PAUSED attachments by involved platform, preserving
 * the involved-platform order and the API's row order within a platform.
 * Platforms with zero candidates are omitted (not connected → not involved).
 */
export function groupAttachments(
  integrations: readonly AttachmentRow[],
  involvedPlatforms: readonly string[],
): PlatformAttachmentGroup[] {
  const groups: PlatformAttachmentGroup[] = [];
  for (const platform of involvedPlatforms) {
    const attachments = integrations.filter(
      (i) => i.type === platform && isCandidate(i),
    );
    if (attachments.length > 0) groups.push({ platform, attachments });
  }
  return groups;
}

/** Total non-paused attachments across the involved platforms (the indicator's N). */
export function countAttachments(
  groups: readonly PlatformAttachmentGroup[],
): number {
  return groups.reduce((sum, g) => sum + g.attachments.length, 0);
}

/**
 * Indicator visibility (AC #1): at least one involved platform has 2+
 * non-paused attachments — that is where account-level aggregation happens.
 * (Two single-attachment platforms on one chart aggregate METRICS, not
 * accounts — no indicator.)
 */
export function hasMultiAttachmentPlatform(
  groups: readonly PlatformAttachmentGroup[],
): boolean {
  return groups.some((g) => g.attachments.length >= 2);
}

/**
 * The stored override, normalized: a non-empty DEDUPED array of strings, else
 * undefined. `[]` mirrors the server's normalization ("ALL accounts" — 31-30
 * review F4); junk elements are dropped; duplicates (hand-edited configs) are
 * collapsed so they can never double-count toward the 20-id schema cap
 * (review R1-2).
 */
export function normalizeStoredIntegrationIds(
  value: unknown,
): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = [
    ...new Set(value.filter((v): v is string => typeof v === "string" && v.length > 0)),
  ];
  return ids.length > 0 ? ids : undefined;
}

/**
 * K of the "Using K of N accounts" copy (AC #2): stored ids that exist in the
 * CURRENT attachment set. Stale ids (since-disconnected accounts) never
 * inflate K (AC #9).
 */
export function countSelectedExisting(
  groups: readonly PlatformAttachmentGroup[],
  storedIds: readonly string[],
): number {
  const stored = new Set(storedIds);
  let count = 0;
  for (const g of groups) {
    for (const a of g.attachments) {
      if (stored.has(a.id)) count++;
    }
  }
  return count;
}

/**
 * The checked-id set the picker renders. No override → everything checked
 * (default-all). Override → stored ∩ current (stale ids invisible, AC #9);
 * single-attachment platforms render checked regardless (pinned, AC #6b).
 */
export function deriveCheckedIds(
  groups: readonly PlatformAttachmentGroup[],
  storedIds: readonly string[] | undefined,
): Set<string> {
  const checked = new Set<string>();
  const stored = storedIds ? new Set(storedIds) : null;
  for (const g of groups) {
    const pinned = g.attachments.length === 1;
    for (const a of g.attachments) {
      if (pinned || !stored || stored.has(a.id)) checked.add(a.id);
    }
  }
  return checked;
}

// ============================================================================
// Write decision (AC #5, #6b, #7)
// ============================================================================

/**
 * Decide what a toggle interaction writes (AC #5):
 *
 * - all attachments effectively selected → `delete` (the key is removed; the
 *   default-all aggregate auto-includes the client's NEXT attachment — a full
 *   explicit list would silently pin it out, AC #5a).
 * - proper subset → `write` with the selected row UUIDs ∪ the pinned ids of
 *   single-attachment involved platforms (AC #6b — every involved platform
 *   contributes ≥1 id, so cross-platform widgets never silently drop one).
 * - union > 20 → `too-many` (schema cap, AC #5e) — caller shows the hint and
 *   does not write.
 *
 * Defensive guarantees (the UI's min-1 rule makes these unreachable, but the
 * algebra never trusts its caller): a multi-attachment platform whose
 * selection went empty is restored to ALL of its ids (default semantics for
 * that platform — never an empty intersection), and the result of `write` is
 * structurally non-empty (`[]` is unrepresentable, 31-30 review F4).
 */
export function computeSelectionPatch(
  groups: readonly PlatformAttachmentGroup[],
  selectedIds: ReadonlySet<string>,
): SelectionPatchResult {
  if (groups.length === 0) return { kind: "delete" };

  const effective: string[] = [];
  let allOn = true;
  for (const g of groups) {
    const pinned = g.attachments.length === 1;
    const groupSelected = g.attachments.filter(
      (a) => pinned || selectedIds.has(a.id),
    );
    if (groupSelected.length === 0) {
      // Unreachable via the UI (min-1 Switch disable) — restore default-all
      // for this platform rather than write an empty intersection.
      effective.push(...g.attachments.map((a) => a.id));
      continue;
    }
    if (groupSelected.length < g.attachments.length) allOn = false;
    effective.push(...groupSelected.map((a) => a.id));
  }

  if (allOn) return { kind: "delete" };
  if (effective.length > INTEGRATION_IDS_MAX) return { kind: "too-many" };
  if (effective.length === 0) return { kind: "delete" }; // never write []
  return { kind: "write", integrationIds: effective };
}

/**
 * Min-1 rule (AC #7): true when this attachment's Switch must be disabled
 * because it is the LAST checked one of a multi-attachment platform group.
 * (Single-attachment groups are pinned/disabled separately, AC #6b.)
 */
export function isLastCheckedInGroup(
  group: PlatformAttachmentGroup,
  checkedIds: ReadonlySet<string>,
  attachmentId: string,
): boolean {
  if (!checkedIds.has(attachmentId)) return false;
  const checkedInGroup = group.attachments.filter((a) => checkedIds.has(a.id));
  return checkedInGroup.length === 1 && checkedInGroup[0]!.id === attachmentId;
}

// ============================================================================
// Metric-add reconciliation (AC #6c)
// ============================================================================

/**
 * When an override is present and a newly-added metric introduces a platform
 * with NO id in the list, auto-append ALL of that platform's non-paused
 * attachment ids — preserving the default-all semantics for the newcomer
 * (without this, the new platform silently contributes nothing on
 * cross-platform paths).
 *
 * Returns:
 * - the SAME array reference when the platform is already represented (or has
 *   no candidates) — caller detects identity and writes nothing;
 * - the appended list otherwise;
 * - `undefined` when the appended union would exceed the 20-id schema cap —
 *   the override is dropped entirely (the default-all aggregate ALSO includes
 *   the new platform, so deleting the key is the correct degrade; a >20 list
 *   would fail report-save validation).
 */
export function reconcileOnMetricAdd(
  currentIds: string[],
  newPlatform: string,
  integrations: readonly AttachmentRow[],
): string[] | undefined {
  const platformAttachments = integrations.filter(
    (i) => i.type === newPlatform && isCandidate(i),
  );
  if (platformAttachments.length === 0) return currentIds;
  const current = new Set(currentIds);
  if (platformAttachments.some((a) => current.has(a.id))) return currentIds;
  const appended = [...currentIds, ...platformAttachments.map((a) => a.id)];
  if (appended.length > INTEGRATION_IDS_MAX) return undefined;
  return appended;
}
